import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled, { createGlobalStyle, css, ThemeContext, ThemeProvider } from 'styled-components';
import {
  dimensions,
  PrintContainerDimensions,
  StudioAllocationPanel,
  StudioContext,
  UnsavedChangesTrigger,
  StudioShimmerBlock,
  StudioSidePanelContext,
  AgGridThemeOverrides,
  StudioPrivatesAllocationPanel,
} from 'venn-components';
import { useHasFF, useRedrawCharts } from 'venn-utils';
import type { Theme } from 'venn-ui-kit';
import { ColorUtils, Display1, ExternalActivityListener, GetColor, getTypographyTheme } from 'venn-ui-kit';
import BuilderList from './BuilderList';
import { StudioRightPanel } from './studio-right-panel';
import { Content, ContentCover, ContentStyle, LeftPanel, PageContent, Panel, TopControl } from './shared';
import { StudioMainLeftPanel } from './studio-main-left-panel';
import TopBar from './top-bar/TopBar';
import { TOP_TOOLBAR_HEIGHT } from './top-bar/shared';
import SetupConfigurationModal from './setup-configuration-modal/SetupConfigurationModal';
import NewReportModal from './new-report-modal/NewReportModal';
import { FirstTimeAISModal } from './FirstTimeAISModal';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  studioIsPortrait,
  studioLeftPanelOpen,
  studioRightPanelOpen,
  studioPrintOrientationType,
  viewPages,
  openAllocatorSubject,
  compareColumnOpen,
  hasAllocationPercentError,
  viewInputManagementVersion,
  hasUnsavedPortfolioChangesInAllocator,
  useRecoilValueWithDefault,
  studioLeftBlockConfigPanelExpandedSelector,
  CONTENT_PADDING,
  selectedBlockIdState,
  isReportState,
  openPrivateAllocatorPortfolio,
  hasUnsavedChangesInPrivatesAllocator,
  SyncSubjectsToCustomFields,
} from 'venn-state';
import { StudioBlockConfigPanelContent } from './studio-right-panel/StudioBlockConfigPanelContent';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { GRID_LAYOUT_BLOCK_CLASS } from './pages/GridPage';
import { triggerResizeEvent } from '../logic/useSelectionSystem';
import { useVirtualization } from '../logic/useVirtualization';
import { InaccessibleSubjectsWarningModal } from './InaccessibleSubjectsWarningModal';
import { DebugPrintPage } from './debug-print-page/DebugPrintPage';

const allBlocksShimmer = <StudioShimmerBlock height={400} />;

/** Note: individual context props are passed in one-by-one to prevent unnecessary rerenders from studio context from propagating unnecessarily. */
// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
const StudioMainContent = React.memo((props: { inPrintMode: boolean; isReport: boolean }) => {
  const { onSelectGlobal } = useContext(StudioSidePanelContext);
  const theme = useContext(ThemeContext);
  const isVirtualized = useVirtualization();
  const studioTheme: Theme = useMemo(() => {
    const selectedStudioTheme = window.sessionStorage.getItem('studioTheme') ?? 'default';
    return {
      ...theme,
      Typography: getTypographyTheme(selectedStudioTheme),
    };
  }, [theme]);

  const [clickedOnContentWrapper, setClickedOnContentWrapper] = useState(false);
  const inputManagementVersion = useRecoilValue(viewInputManagementVersion);

  const onExternalActivity = useCallback(() => {
    setClickedOnContentWrapper(false);
  }, []);

  const onContentMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      // TODO(will): IMV2 remove this event listener
      if (inputManagementVersion === 2) return;
      const element = event.target as HTMLElement;
      const interactingWithGridBlock = element.closest(`.${GRID_LAYOUT_BLOCK_CLASS}`);
      setClickedOnContentWrapper(!interactingWithGridBlock);
    },
    [inputManagementVersion],
  );

  const onContentClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      // TODO(will): IMV2 remove this event listener
      if (inputManagementVersion === 2) return;
      const element = event.target as HTMLElement;
      const interactingWithGridBlock = element.closest(`.${GRID_LAYOUT_BLOCK_CLASS}`);
      // Prevent markdown becoming unselected if end of text highlight falls outside of block
      if (window.getSelection()?.type !== 'Range' && !interactingWithGridBlock && clickedOnContentWrapper) {
        onSelectGlobal();
      }
      setClickedOnContentWrapper(false);
      triggerResizeEvent();
    },
    [clickedOnContentWrapper, inputManagementVersion, onSelectGlobal],
  );

  const builderListWithDimensions = React.useCallback(
    ({ width }: { width: number; print: boolean }): JSX.Element => (
      <BuilderList width={Math.floor(width - (props.inPrintMode ? 0 : CONTENT_PADDING * 2))} />
    ),
    [props.inPrintMode],
  );

  return (
    <ThemeProvider theme={studioTheme}>
      <StyledExternalActivityListener allowOverflow={!isVirtualized} onExternalActivity={onExternalActivity}>
        <Content onMouseDown={onContentMouseDown} onClick={onContentClick}>
          <React.Suspense fallback={allBlocksShimmer}>
            <Blocks inPrintMode={props.inPrintMode}>
              <DebugPrintPage />
              <PrintContainerDimensions>{builderListWithDimensions}</PrintContainerDimensions>
            </Blocks>
          </React.Suspense>
        </Content>
      </StyledExternalActivityListener>
    </ThemeProvider>
  );
});

/** Note: individual context props are passed in one-by-one to prevent unnecessary rerenders caused by studio context from propagating unnecessarily. */
// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
const StudioPanels = React.memo(
  ({
    inPrintMode,
    isLeftBlockConfigPanelOpen,
    isAllocatorPanelOpen,
  }: {
    inPrintMode: boolean;
    isLeftBlockConfigPanelOpen: boolean;
    isAllocatorPanelOpen: boolean;
  }) => {
    const selectedBlockId = useRecoilValue(selectedBlockIdState);
    const inputManagementVersion = useRecoilValue(viewInputManagementVersion);
    const isPrivateAllocatorOpen = !!useRecoilValue(openPrivateAllocatorPortfolio);
    const hasAllocationError = useRecoilValue(hasAllocationPercentError);
    return (
      <>
        {!inPrintMode && inputManagementVersion === 1 && (
          <Panel data-testid="qa-right-panel">
            <StudioRightPanel />
          </Panel>
        )}
        {!inPrintMode && (
          <LeftPanel>
            <StudioMainLeftPanel />
          </LeftPanel>
        )}
        {!inPrintMode && selectedBlockId && isLeftBlockConfigPanelOpen && (
          <BlockConfigPanelWrapper>
            <StudioBlockConfigPanelContent selectedBlockId={selectedBlockId} />
          </BlockConfigPanelWrapper>
        )}
        {!inPrintMode && isAllocatorPanelOpen && (
          <>
            <AllocationPanelWrapper>
              {isPrivateAllocatorOpen ? <StudioPrivatesAllocationPanel /> : <StudioAllocationPanel />}
            </AllocationPanelWrapper>
            {hasAllocationError && (
              <ContentCover>
                <Display1>To resume analysis, ensure investment allocations sum up to 100%</Display1>
              </ContentCover>
            )}
          </>
        )}
      </>
    );
  },
);

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
const StudioContent = React.memo(() => {
  const isMainLeftPanelOpen = useRecoilValue(studioLeftPanelOpen);
  const isRightPanelOpen = useRecoilValue(studioRightPanelOpen);
  const reportLabPages = useRecoilValue(viewPages);
  const hasUnsavedReturnsAllocatorChanges = useRecoilValueWithDefault(hasUnsavedPortfolioChangesInAllocator, false);
  const hasUnsavedPrivateAllocatorChanges = useRecoilValueWithDefault(hasUnsavedChangesInPrivatesAllocator, false);
  const hasUnsavedAllocatorChanges = hasUnsavedReturnsAllocatorChanges || hasUnsavedPrivateAllocatorChanges;
  const {
    hasUnsavedChange,
    afterUnsavedChangesAction,
    inPrintMode,
    isOpenReportConfigModal,
    openReportConfigModal,
    closeReportConfigModal,
    isOpenNewReportModal,
    closeNewReportModal,
    analysisView,
  } = useContext(StudioContext);
  const isReport = useRecoilValue(isReportState);

  const subjectInAllocator = useRecoilValue(openAllocatorSubject);
  const privatePortfolioInAllocator = useRecoilValue(openPrivateAllocatorPortfolio);

  const isCompareOpen = useRecoilValue(compareColumnOpen);
  const isAllocatorPanelOpen = !isReport && (!!subjectInAllocator || !!privatePortfolioInAllocator);
  const inputManagementVersion = useRecoilValue(viewInputManagementVersion);
  const selectedBlockId = useRecoilValue(selectedBlockIdState);
  const isLeftBlockConfigPanelOpen = !!selectedBlockId && inputManagementVersion === 2 && !isAllocatorPanelOpen;
  const hasAIS = inputManagementVersion === 2;
  const isOriginalAISOrg = useHasFF('original_ais_org');

  const [isLeftBlockConfigPanelExpanded, setIsLeftBlockConfigPanelExpanded] = useRecoilState(
    studioLeftBlockConfigPanelExpandedSelector,
  );
  useEffect(() => {
    if (isLeftBlockConfigPanelOpen) {
      // When the panel gets opened from being closed previously, expand it by default
      setIsLeftBlockConfigPanelExpanded(true);
    }
  }, [isLeftBlockConfigPanelOpen, setIsLeftBlockConfigPanelExpanded]);

  const printOrientationType = useRecoilValue(studioPrintOrientationType);
  const isPortrait = useRecoilValue(studioIsPortrait);
  const pageSize = `A4 ${isPortrait ? 'portrait' : 'landscape'}`;

  const { totalHeight: pageHeight } = dimensions[printOrientationType];
  const printContentHeight = isReport ? reportLabPages.length * pageHeight : undefined;

  useRedrawCharts(isLeftBlockConfigPanelOpen, isAllocatorPanelOpen, isLeftBlockConfigPanelExpanded);

  return (
    <UnsavedChangesTrigger isNotSaved={hasUnsavedChange || hasUnsavedAllocatorChanges} ignoreSearchParam>
      {({ isOpen: isUnsavedChangesModalOpen, handleConfirmNavigation, handleReject }) => (
        <>
          {hasAIS && !isOriginalAISOrg && <FirstTimeAISModal />}
          {(isUnsavedChangesModalOpen || afterUnsavedChangesAction) && (
            <UnsavedChangesModal onProceed={handleConfirmNavigation} onReject={handleReject} />
          )}
          <SyncSubjectsToCustomFields />
          <ContentWrapper height={printContentHeight}>
            <StudioStyle pageSize={pageSize} />
            <AgGridThemeOverrides />
            <PageContent
              isRightPanelEnabled={inputManagementVersion === 1}
              isRightPanelOpen={isRightPanelOpen}
              isMainLeftPanelOpen={isMainLeftPanelOpen}
              isAllocatorOpen={isAllocatorPanelOpen}
              isCompareOpen={isCompareOpen}
              isLeftBlockConfigPanelOpen={isLeftBlockConfigPanelOpen}
              isLeftBlockConfigPanelExpanded={isLeftBlockConfigPanelExpanded}
              inPrintMode={inPrintMode}
              orientation={printOrientationType}
            >
              {!inPrintMode && (
                <TopControl>
                  <SaveWrapper>
                    <TopBar openReportConfigModal={openReportConfigModal} />
                  </SaveWrapper>
                </TopControl>
              )}

              <StudioMainContent inPrintMode={inPrintMode} isReport={!!isReport} />

              <StudioPanels
                inPrintMode={inPrintMode}
                isLeftBlockConfigPanelOpen={isLeftBlockConfigPanelOpen}
                isAllocatorPanelOpen={isAllocatorPanelOpen}
              />

              {!inPrintMode && isOpenReportConfigModal && <SetupConfigurationModal onCancel={closeReportConfigModal} />}
              {!inPrintMode && isOpenNewReportModal && <NewReportModal onCancel={closeNewReportModal} />}
              {analysisView?.id && <InaccessibleSubjectsWarningModal viewId={analysisView.id} />}
            </PageContent>
          </ContentWrapper>
        </>
      )}
    </UnsavedChangesTrigger>
  );
});

export default StudioContent;

const secondaryLeftPanelMixin = css`
  background-color: ${GetColor.WhiteGrey};
  padding-left: 7px;
  height: 100%;
`;

const BlockConfigPanelWrapper = styled.div`
  ${secondaryLeftPanelMixin};
  grid-area: blockConfigPanel;
  overflow: auto;
`;

const AllocationPanelWrapper = styled.div`
  ${secondaryLeftPanelMixin};

  grid-area: allocator;
  position: relative;
  width: unset;
  display: flex;
  overflow: hidden;
`;

const SaveWrapper = styled.div`
  height: ${TOP_TOOLBAR_HEIGHT};
`;

const Blocks = styled.div<{ inPrintMode?: boolean }>`
  background-color: ${ColorUtils.hex2rgbaFrom(GetColor.WhiteGrey, 0.5)};
  ${({ inPrintMode }) =>
    inPrintMode &&
    css`
      background-color: transparent;
      padding: 0;
    `}
`;

const ContentWrapper = styled.div<{ height?: number }>`
  overflow-x: hidden;
  position: relative;
  margin: 0 auto;
  width: 100%;

  @media print {
    display: block !important;
  }
`;

const StudioStyle = createGlobalStyle<{ pageSize: string }>`
  /* Printing */
  @media print {
    size: ${({ pageSize }) => pageSize};
  }

  @page {
    size: ${({ pageSize }) => pageSize};
    margin: 0;
    padding: 0;
  }
`;

const StyledExternalActivityListener = styled(ExternalActivityListener)<{ allowOverflow: boolean }>`
  ${({ allowOverflow }) =>
    allowOverflow &&
    css`
      overflow-y: auto;
    `}
  ${ContentStyle}
`;
